import { INSPECTION_ITEM_RESPONSE, INSPECTION_ITEM_TYPE } from '@/_legacy/constants';
import { isEntityPropertyPrefillEnabled } from '@/ui/helpers/featureHelper';

const EMPTY_VALUE = '';

interface PrefillState {
  response: string;
  responded: boolean;
  prefillReadOnly: boolean;
  prefillHidden: boolean;
}

interface OptionValue {
  id: number;
  label: string;
  value: string | number | boolean | null;
  isActive: boolean;
}

interface EntityItemProperty {
  id: number;
  entityTypePropertyId: number;
  valueText: string | null;
  optionValueIds: number[];
  optionValues: OptionValue[];
}

interface EntityItemWithProperties {
  data?: {
    properties?: EntityItemProperty[];
  };
}

interface QuestionItem {
  type?: string;
  response_type?: string;
  response?: string;
  html_response?: string;
  rte_response?: boolean;
  responded?: boolean;
  params?: {
    prefill_entity_property_id?: number;
    allow_edit_prefilled_value?: boolean;
    hide_field_from_users?: boolean;
    show_rte_response?: boolean;
  };
}

const ensureString = (value: string | null | undefined): string => {
  if (typeof value === 'string') {
    return value;
  }

  return EMPTY_VALUE;
};

const formatDropdownValue = (optionValues: OptionValue[]): string => {
  if (!Array.isArray(optionValues)) {
    return EMPTY_VALUE;
  }

  return optionValues
    .map(option => ensureString(option?.label))
    .filter(label => label.length > 0)
    .join(', ');
};

const extractPropertyById = (entityItem: EntityItemWithProperties | null | undefined, propertyId: number): EntityItemProperty | null => {
  if (!entityItem?.data?.properties) {
    return null;
  }

  const numericPropertyId = Number(propertyId);

  return entityItem.data.properties.find(
    prop => prop?.entityTypePropertyId === numericPropertyId,
  ) ?? null;
};

export const isTextPrefillQuestion = (item: QuestionItem | null | undefined): boolean => {
  if (!isEntityPropertyPrefillEnabled()) {
    return false;
  }

  if (!item) {
    return false;
  }

  const isQuestion = item.type === INSPECTION_ITEM_TYPE.QUESTION;
  if (!isQuestion) {
    return false;
  }

  const isTextResponse = item.response_type === INSPECTION_ITEM_RESPONSE.TEXT;
  if (!isTextResponse) {
    return false;
  }

  const hasPrefillProperty = Boolean(item?.params?.prefill_entity_property_id);
  const hasReadOnlyFlag = item?.params?.allow_edit_prefilled_value === false;
  const hasHiddenFlag = Boolean(item?.params?.hide_field_from_users);

  return hasPrefillProperty || hasReadOnlyFlag || hasHiddenFlag;
};

export const computeTextPrefill = (
  question: QuestionItem,
  entityItem: EntityItemWithProperties | null | undefined,
): PrefillState => {
  const allowEdit = question.params?.allow_edit_prefilled_value !== false;
  const hideField = Boolean(question.params?.hide_field_from_users);

  if (!question?.params?.prefill_entity_property_id) {
    return {
      response: EMPTY_VALUE,
      responded: false,
      prefillReadOnly: !allowEdit,
      prefillHidden: hideField,
    };
  }

  const property = extractPropertyById(entityItem, question.params.prefill_entity_property_id);
  if (!property) {
    return {
      response: EMPTY_VALUE,
      responded: false,
      prefillReadOnly: !allowEdit,
      prefillHidden: hideField,
    };
  }

  const isDropdown = Array.isArray(property.optionValues) && property.optionValues.length > 0;
  const rawValue = isDropdown
    ? formatDropdownValue(property.optionValues)
    : ensureString(property.valueText);

  const value = ensureString(rawValue);
  const hasValue = value.length > 0;

  return {
    response: value,
    responded: hasValue,
    prefillReadOnly: !allowEdit,
    prefillHidden: hideField,
  };
};
